// components/ui/Dropdown.tsx
import React, {
  FC,
  ReactNode,
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
} from "react";
import { useOnClickOutside } from "../../hooks/useClickOutside";

type DropdownProps = {
  trigger: ReactNode;
  children: ReactNode;
  className?: string;
  position?: "left" | "right";
  label?: string;
};

export const Dropdown: FC<DropdownProps> = ({
  trigger,
  children,
  className = "",
  position = "right",
  label = "Menu",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  const openMenu = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsOpen(true);
  };

  const closeMenu = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setIsOpen(false), 100);
  };

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (["Enter", " ", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const firstItem = menuRef.current?.querySelector(
        '[role="menuitem"]'
      ) as HTMLElement;
      firstItem?.focus();
    }
  }, [isOpen]);

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
      onFocus={openMenu}
      onBlur={closeMenu}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="dropdown-menu"
        onClick={handleToggle}
        onKeyDown={handleTriggerKey}
        className="focus:outline-none"
      >
        {trigger}
      </button>

      {isOpen && (
        <div
          id="dropdown-menu"
          role="menu"
          aria-label={label}
          ref={menuRef}
          className={`absolute text-sm mt-2 z-10 bg-white border border-neutrals-300 rounded shadow-md ${
            position === "right" ? "right-0" : "left-0"
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
};
